import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Seo from "@/components/feature/Seo";
import FaqAccordion from "@/components/feature/FaqAccordion";
import SectionTitle from "@/components/feature/SectionTitle";
import { siteMeta, siteStats, siteValues, voices } from "@/mocks/site";
import { aboutFaqs } from "@/mocks/faqs";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于爆料91",
    description:
      "了解爆料91的品牌定位、内容原则与联系方式，我们专注整理吃瓜现场之外的网友讨论与舆论反应。",
    url: "https://gaohaoshuzi.cn/about",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteMeta.name,
    url: "https://gaohaoshuzi.cn/",
    email: siteMeta.email,
    description: siteMeta.intro,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://gaohaoshuzi.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://gaohaoshuzi.cn/about" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutFaqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
];

export default function About() {
  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="关于我们 · 爆料91的内容原则与联系方式"
        description="了解爆料91的品牌定位、内容整理原则、用户口碑与联系方式。我们专注把吃瓜现场之外的网友讨论、评论区观点与舆论反应讲清楚。"
        keywords="爆料91,爆料91网友讨论,爆料91评论区,关于爆料91"
        path="/about"
        ogType="website"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 lg:px-12 pt-6 pb-10 md:pb-14 bg-background-100">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-bold">
              <i className="ri-information-line"></i>
              关于我们
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-black text-foreground-950 font-heading leading-tight">
              把评论区的声音，讲得更清楚一点
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              爆料91从 {siteMeta.since} 年开始做内容整理，我们不做单向的曝料，而是把公开讨论整理成可读的话题，让围观这件事不至于只剩下情绪。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="w-full aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20toned%20editorial%20illustration%20of%20a%20cozy%20content%20editing%20desk%20with%20open%20notebooks%20and%20a%20warm%20lamp%2C%20soft%20ambient%20light%2C%20creamy%20background%2C%20minimal%20composition%2C%20amber%20and%20coral%20palette&width=800&height=960&seq=bl91-about-desk&orientation=portrait"
                alt="内容整理工作台插画"
                title="爆料91 内容整理"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-black text-foreground-950 font-heading leading-snug">
                我们的定位
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                {siteMeta.intro}
              </p>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                对网友来说，吃瓜最难的部分往往不是事件本身，而是信息太碎、立场太多。爆料91做的事情很具体：把同一个话题下的讨论归类、精简、标注时间，让后来的人能快速跟上，而不是从头翻评论区。
              </p>

              <dl className="mt-8 grid grid-cols-2 gap-4">
                {siteStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl bg-background-100 border border-background-200 px-4 py-4"
                  >
                    <dt className="text-xl md:text-2xl font-black text-primary-600 font-heading">
                      {stat.value}
                    </dt>
                    <dd className="mt-1 text-xs md:text-sm text-foreground-600">{stat.label}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
          <SectionTitle
            eyebrow="内容原则"
            title="我们整理内容时坚持的四件事"
            desc="这些原则决定了哪些讨论会被收录、以什么方式呈现，也决定了我们不去做什么。"
          />
          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {siteValues.map((value, index) => (
              <div
                key={value.title}
                className={`rounded-2xl p-5 md:p-6 border ${
                  index % 2 === 1
                    ? "bg-secondary-50 border-secondary-200"
                    : "bg-background-50 border-background-200"
                }`}
              >
                <span className="w-10 h-10 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${value.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{value.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <SectionTitle
            eyebrow="用户口碑"
            title="他们这样使用爆料91"
            desc="来自不同板块的常驻网友反馈，看看大家都在哪个环节用上了它。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {voices.map((voice) => (
              <figure
                key={voice.name}
                className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6"
              >
                <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-lg"></i>
                </span>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                  {voice.content}
                </blockquote>
                <figcaption className="mt-4 pt-4 border-t border-background-200">
                  <p className="text-sm font-bold text-foreground-950">{voice.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{voice.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 pb-14 md:pb-20">
          <div className="rounded-3xl bg-primary-950 text-background-100 px-6 py-10 md:px-12 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-background-50 font-heading leading-snug">
                  联系我们
                </h2>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-background-200/80">
                  有线索、有建议，或者希望和我们一起整理讨论内容，都可以通过下面的方式找到我们。我们不接受隐私信息的投稿，也不对未经证实的指控下定论。
                </p>
                <div className="mt-6 flex flex-col gap-3 text-sm">
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-mail-line text-accent-400"></i>
                    {siteMeta.email}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-time-line text-accent-400"></i>
                    {siteMeta.workingHours}
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link
                  to="/submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-accent-500 text-primary-950 text-sm font-black hover:bg-accent-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-send-plane-fill"></i>
                  投稿爆料线索
                </Link>
                <Link
                  to="/topics"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-background-50/12 border border-background-50/30 text-background-50 text-sm font-bold hover:bg-background-50/20 transition-colors whitespace-nowrap"
                >
                  浏览热议话题
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-14">
            <SectionTitle
              eyebrow="常见问题"
              title="关于爆料91，你可能还想知道"
              desc="如果这里没有你要的答案，欢迎通过投稿页面或邮箱联系我们。"
            />
            <FaqAccordion items={aboutFaqs} />
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}